class ProductRecommendations extends HTMLElement {
  static #REBUY_UUID_STORAGE_KEY = 'rebuy_uuid';

  /**
   * The observer for the product recommendations
   * @type {IntersectionObserver}
   */
  #intersectionObserver = new IntersectionObserver(
    (entries, observer) => {
      if (!entries[0]?.isIntersecting) return;

      observer.disconnect();
      this._loadRecommendations();
    },
    { rootMargin: '0px 0px 400px 0px' }
  );

  /**
   * Observing changes to the elements attributes
   * @type {MutationObserver}
   */
  #mutationObserver = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      // Only attribute changes are interesting
      if (mutation.target !== this || mutation.type !== 'attributes') continue;

      // Ignore error attribute changes
      if (mutation.attributeName === 'data-error') continue;

      // Ignore add-on slider UI state changes
      if (
        mutation.attributeName === 'data-add-on-slider-at-start' ||
        mutation.attributeName === 'data-add-on-slider-at-end'
      )
        continue;

      // Ignore addition of hidden class because it means there's an error with the display
      if (mutation.attributeName === 'class' && this.classList.contains('hidden')) continue;

      // Ignore when the data-recommendations-performed attribute has been set to 'true'
      if (
        mutation.attributeName === 'data-recommendations-performed' &&
        this.dataset.recommendationsPerformed === 'true'
      )
        continue;

      // All other attribute changes trigger a reload
      this._loadRecommendations();
      break;
    }
  });

  /**
   * The cached recommendations
   * @type {Record<string, string>}
   */
  #cachedRecommendations = {};

  /**
   * An abort controller for the active fetch (if there is one)
   * @type {AbortController | null}
   */
  #activeFetch = null;

  constructor() {
    super();
    this._addOnSliderButtons = [];
    this._handleAddOnSliderClick = this._handleAddOnSliderClick.bind(this);
    this._updateAddOnSliderControls = this._updateAddOnSliderControls.bind(this);
  }

  connectedCallback() {
    this.#intersectionObserver.observe(this);
    this.#mutationObserver.observe(this, { attributes: true });
    this._bindAddOnSliderControls();
  }

  disconnectedCallback() {
    this._addOnSliderButtons.forEach((button) => {
      button.removeEventListener('click', this._handleAddOnSliderClick);
    });
    this._addOnSliderButtons = [];
  }

  /**
   * Load the product recommendations
   */
  _loadRecommendations() {
    const dataSource = this.dataset.dataSource;

    if (dataSource === 'recently_viewed') {
      this._loadRecentlyViewedProducts();
      return;
    }

    if (this.dataset.rebuyUrl) {
      this._loadRebuyRecommendations();
      return;
    }

    const { productId, recommendationsPerformed, sectionId, intent } = this.dataset;
    const id = this.id;

    if (!productId || !id) {
      throw new Error('Product ID and an ID attribute are required');
    }

    // If the recommendations have already been loaded, accounts for the case where the Theme Editor
    // is loaded the section from the editor's visual preview context.
    if (recommendationsPerformed === 'true') {
      return;
    }

    this._fetchCachedRecommendations(productId, sectionId, intent)
      .then((result) => {
        if (!result.success) {
          // The Theme Editor will place a section element element in the DOM whose section_id is not available
          // to the Section Renderer API. In this case, we can safely ignore the error.
          if (!Shopify.designMode) {
            this._handleError(new Error(`Server returned ${result.status}`));
          }
          return;
        }

        const html = document.createElement('div');
        html.innerHTML = result.data || '';
        const recommendations = html.querySelector(`product-recommendations[id="${id}"]`);

        if (recommendations?.innerHTML && recommendations.innerHTML.trim().length) {
          this.dataset.recommendationsPerformed = 'true';
          this.innerHTML = recommendations.innerHTML;
        } else {
          this._handleError(new Error('No recommendations available'));
        }
      })
      .catch((e) => {
        this._handleError(e);
      });
  }

  /**
   * Load Rebuy-powered recommendations
   */
  async _loadRebuyRecommendations() {
    const rebuyUrl = this.dataset.rebuyUrl;
    const id = this.id;

    if (!rebuyUrl || !id) return;

    if (this.dataset.recommendationsPerformed === 'true') {
      return;
    }

    const fetchUrl = this._buildRebuyEndpointUrl(rebuyUrl);

    this.#activeFetch?.abort();
    this.#activeFetch = new AbortController();

    try {
      const response = await fetch(fetchUrl, { signal: this.#activeFetch.signal });
      if (!response.ok) {
        this._handleError(new Error(`Server returned ${response.status}`));
        return;
      }

      const data = await response.json();
      const rebuyProducts = data?.data || [];
      let handles = rebuyProducts.map((item) => item.handle).filter(Boolean);

      const maxProducts = parseInt(this.dataset.maxProducts, 10);
      if (maxProducts > 0) {
        handles = handles.slice(0, maxProducts);
      }

      if (!handles.length) {
        this._handleError(new Error('No recommendations available'));
        return;
      }

      const searchUrl = this._buildRebuySearchUrl(handles);
      const cachedResponse = this.#cachedRecommendations[searchUrl];
      let text = cachedResponse;

      if (!text) {
        const viewResponse = await fetch(searchUrl, { signal: this.#activeFetch.signal });
        if (!viewResponse.ok) {
          this._handleError(new Error(`Server returned ${viewResponse.status}`));
          return;
        }

        text = await viewResponse.text();
        this.#cachedRecommendations[searchUrl] = text;
      }

      const html = document.createElement('div');
      html.innerHTML = text || '';

      const rebuyContent = html.querySelector('[data-rebuy-items]');
      if (!rebuyContent) {
        this._handleError(new Error('No recommendations available'));
        return;
      }

      this._syncRebuyAddOnContent(rebuyContent, rebuyProducts, handles);

      const target = this.querySelector('[data-rebuy-items]');
      if (target) {
        target.replaceWith(rebuyContent);
      } else {
        this.innerHTML = rebuyContent.outerHTML;
      }

      this._refreshHeadingSliderControls();
      this._bindAddOnSliderControls();

      this.dataset.recommendationsPerformed = 'true';
      this.classList.remove('hidden');
      this.removeAttribute('data-error');

      if (rebuyContent.dataset.hasRecommendations === 'false') {
        this.classList.add('hidden');
      }
    } catch (error) {
      if (error?.name === 'AbortError') return;
      this._handleError(error);
    } finally {
      this.#activeFetch = null;
    }
  }

  /**
   * Load recently viewed products
   */
  async _loadRecentlyViewedProducts() {
    if (this.dataset.recommendationsPerformed === 'true') return;

    this.#activeFetch?.abort();
    this.#activeFetch = new AbortController();

    try {
      const { RecentlyViewed } = await import(
        new URL('recently-viewed-products.js', import.meta.url).href
      );
      let handles = RecentlyViewed.getHandles();

      const maxProducts = parseInt(this.dataset.maxProducts, 10);
      if (maxProducts > 0) {
        handles = handles.slice(0, maxProducts);
      }

      if (!handles.length) {
        this._handleError(new Error('No recently viewed products'));
        return;
      }

      const searchUrl = this._buildRebuySearchUrl(handles);
      const cachedResponse = this.#cachedRecommendations[searchUrl];
      let text = cachedResponse;

      if (!text) {
        const viewResponse = await fetch(searchUrl, { signal: this.#activeFetch.signal });
        if (!viewResponse.ok) {
          this._handleError(new Error(`Server returned ${viewResponse.status}`));
          return;
        }
        text = await viewResponse.text();
        this.#cachedRecommendations[searchUrl] = text;
      }

      const html = document.createElement('div');
      html.innerHTML = text || '';

      const dynamicContent = html.querySelector('[data-rebuy-items]');
      if (!dynamicContent) {
        this._handleError(new Error('No recently viewed products available'));
        return;
      }

      const target = this.querySelector('[data-rebuy-items]');
      if (target) {
        target.replaceWith(dynamicContent);
      } else {
        this.innerHTML = dynamicContent.outerHTML;
      }

      this._refreshHeadingSliderControls();
      this._bindAddOnSliderControls();
      this.dataset.recommendationsPerformed = 'true';
      this.classList.remove('hidden');
      this.removeAttribute('data-error');

      if (dynamicContent.dataset.hasRecommendations === 'false') {
        this.classList.add('hidden');
      }
    } catch (error) {
      if (error?.name === 'AbortError') return;
      this._handleError(error);
    } finally {
      this.#activeFetch = null;
    }
  }

  _buildRebuyEndpointUrl(rebuyUrl) {
    const endpointUrl = new URL(rebuyUrl);
    const productIds = this.getAttribute('shopify_product_ids');
    if (productIds) {
      endpointUrl.searchParams.set('shopify_product_ids', productIds);
    }

    const uuid = this._getRebuyUuid();
    if (uuid) {
      endpointUrl.searchParams.set('uuid', uuid);
    }

    return endpointUrl.toString();
  }

  _getRebuyUuid() {
    const rebuyGlobal = window.Rebuy || {};
    const uuidCandidates = [
      rebuyGlobal.uuid,
      rebuyGlobal?.customerData?.uuid,
      rebuyGlobal?.metadata?.uuid,
      rebuyGlobal?.session?.uuid,
      window?.rebuy?.uuid,
    ];

    const existingUuid = uuidCandidates.find((value) => typeof value === 'string' && value.trim() !== '');
    if (existingUuid) {
      try {
        localStorage.setItem(ProductRecommendations.#REBUY_UUID_STORAGE_KEY, existingUuid);
      } catch (_error) {
        // Ignore storage failures (private mode / blocked storage)
      }
      return existingUuid;
    }

    try {
      const storedUuid = localStorage.getItem(ProductRecommendations.#REBUY_UUID_STORAGE_KEY);
      if (storedUuid && storedUuid.trim() !== '') return storedUuid;
    } catch (_error) {
      // Ignore storage read failures
    }

    const generatedUuid = this._generateUuid();
    if (!generatedUuid) return null;

    try {
      localStorage.setItem(ProductRecommendations.#REBUY_UUID_STORAGE_KEY, generatedUuid);
    } catch (_error) {
      // Ignore storage write failures
    }

    return generatedUuid;
  }

  _generateUuid() {
    if (window?.crypto?.randomUUID) return window.crypto.randomUUID();

    if (!window?.crypto?.getRandomValues) return null;

    // RFC4122 v4 fallback for browsers without crypto.randomUUID
    const bytes = new Uint8Array(16);
    window.crypto.getRandomValues(bytes);
    bytes[6] = (bytes[6] & 0x0f) | 0x40;
    bytes[8] = (bytes[8] & 0x3f) | 0x80;
    const hex = [...bytes].map((byte) => byte.toString(16).padStart(2, '0')).join('');
    return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
  }

  _refreshHeadingSliderControls() {
    const section = this.closest('[id*="shopify-section"]');
    if (!section) return;

    section.querySelectorAll('heading-slider-controls').forEach((control) => {
      if (typeof control.refresh === 'function') {
        control.refresh();
      } else {
        const slideshow = section.querySelector('slideshow-component');
        if (slideshow) slideshow.setAttribute('data-heading-slider-controls', 'true');
      }
    });
  }

  _bindAddOnSliderControls() {
    this._addOnSliderButtons.forEach((button) => {
      button.removeEventListener('click', this._handleAddOnSliderClick);
    });

    this._addOnSliderButtons = [...this.querySelectorAll('[data-add-on-slider-action]')];
    this._addOnSliderButtons.forEach((button) => {
      button.addEventListener('click', this._handleAddOnSliderClick);
    });

    const slideshow = this._getDesktopAddOnSlideshow();
    if (slideshow) {
      slideshow.removeEventListener('slideshow:select', this._updateAddOnSliderControls);
      slideshow.addEventListener('slideshow:select', this._updateAddOnSliderControls);
    }

    this._updateAddOnSliderControls();
  }

  _getDesktopAddOnSlideshow() {
    return this.querySelector('.rebuy-add-on-recommendations--desktop slideshow-component');
  }

  _handleAddOnSliderClick(event) {
    event.preventDefault();

    const button = event.currentTarget;
    const action = button?.dataset?.addOnSliderAction;
    const slideshow = this._getDesktopAddOnSlideshow();

    if (!slideshow || !action || typeof slideshow[action] !== 'function') return;

    slideshow[action](event);
    this._updateAddOnSliderControls();
  }

  _updateAddOnSliderControls() {
    const slideshow = this._getDesktopAddOnSlideshow();
    this.dataset.addOnSliderAtStart = slideshow ? slideshow.atStart.toString() : 'true';
    this.dataset.addOnSliderAtEnd = slideshow ? slideshow.atEnd.toString() : 'true';

    this._addOnSliderButtons.forEach((button) => {
      const action = button.dataset.addOnSliderAction;
      const isDisabled =
        !slideshow ||
        (action === 'previous' && !slideshow.infinite && slideshow.atStart) ||
        (action === 'next' && !slideshow.infinite && slideshow.atEnd);

      button.disabled = isDisabled;
    });
  }

  _syncRebuyAddOnContent(rebuyContent, rebuyProducts, handles) {
    if (this.dataset.displayMode !== 'add_on') return;

    const orderedProducts = handles
      .map((handle) => rebuyProducts.find((product) => product?.handle === handle))
      .filter(Boolean);

    if (!orderedProducts.length) return;

    const renderedHandles = [...rebuyContent.querySelectorAll('.rebuy-product-add-on-card__title')]
      .map((link) => new URL(link.href, window.location.origin).pathname.split('/products/')[1]?.split('/')[0])
      .filter(Boolean);

    const renderedMatchesRebuy =
      renderedHandles.length === handles.length && handles.every((handle, index) => renderedHandles[index] === handle);

    if (renderedMatchesRebuy) return;

    const slidesMarkup = orderedProducts.map((product, index) => this._buildRebuyAddOnSlide(product, index)).join('');
    if (!slidesMarkup) return;

    rebuyContent.querySelectorAll('.rebuy-add-on-recommendations slideshow-slides').forEach((slides) => {
      slides.innerHTML = slidesMarkup;
    });

    rebuyContent.querySelectorAll('.rebuy-add-on-recommendations').forEach((container) => {
      container.dataset.hasRecommendations = 'true';
    });
    rebuyContent.dataset.hasRecommendations = 'true';
  }

  _buildRebuyAddOnSlide(product, index) {
    return `
      <slideshow-slide
        ref="slides[]"
        aria-hidden="false"
        style="view-timeline-name: --slide-${index}; --product-media-fit: cover;"
        class="resource-list__slide"
      >
        <div class="resource-list__item">
          ${this._buildRebuyAddOnCard(product)}
        </div>
      </slideshow-slide>
    `;
  }

  _buildRebuyAddOnCard(product) {
    const variant = product?.variants?.[0] || {};
    const productUrl = `/products/${this._escapeAttribute(product.handle)}${variant.id ? `?variant=${variant.id}` : ''}`;
    const image = product.image || product.images?.[0] || {};
    const imageSrc = image.src || '';
    const imageAlt = image.alt || product.title || '';
    const title = this._getRebuyAddOnTitle(product.title);
    const subtitle = this._getRebuyAddOnSubtitle(product.title);
    const sizeBadge = this._getRebuyAddOnSize(product.title);
    const price = this._formatRebuyPrice(variant.price || product.price);
    const canAddToCart = Boolean(variant.id) && variant.inventory_quantity !== 0;
    const buttonLabel = this.dataset.addButtonLabel || '+Add';
    const formId = `RebuyAddOn-ProductForm-${product.id}`;
    const productVariantMedia = imageSrc ? this._imageUrlWithWidth(imageSrc, 100) : '';

    return `
      <div class="rebuy-product-add-on-card">
        <a class="rebuy-product-add-on-card__media" href="${productUrl}">
          ${
            imageSrc
              ? `<img
                  src="${this._escapeAttribute(this._imageUrlWithWidth(imageSrc, 160))}"
                  alt="${this._escapeAttribute(imageAlt)}"
                  width="160"
                  height="160"
                  loading="lazy"
                  sizes="80px"
                  class="rebuy-product-add-on-card__image"
                >`
              : ''
          }
        </a>

        <div class="rebuy-product-add-on-card__info">
          <a
            class="text-block h4 rebuy-product-add-on-card__title"
            href="${productUrl}"
            style="--color: var(--color-foreground-heading);"
          >
            <p>${this._escapeHtml(title)}</p>
          </a>

          ${
            subtitle
              ? `<div class="text-block small rebuy-product-add-on-card__subtitle" style="--color: var(--color-foreground);"><p><strong>${this._escapeHtml(subtitle)}</strong></p></div>`
              : ''
          }

          <span
            role="img"
            aria-label="Product Rating"
            class="inline-rating rebuy-product-add-on-card__rating"
            data-bv-show="inline_rating"
            data-bv-product-id="${this._escapeAttribute(product.id)}"
            data-bv-redirect-url="/products/${this._escapeAttribute(product.handle)}"
            data-bv-seo="false"
          ></span>

          <div class="rebuy-product-add-on-card__price">
            ${this._escapeHtml(price)}
          </div>

          ${sizeBadge ? `<span class="rebuy-product-add-on-card__badge">${this._escapeHtml(sizeBadge)}</span>` : ''}
        </div>

        <product-form-component
          class="rebuy-product-add-on-card__form"
          data-section-id="rebuy-add-on"
          data-product-id="${this._escapeAttribute(product.id)}"
          on:submit="/handleSubmit"
        >
          <div
            class="visually-hidden"
            aria-live="assertive"
            role="status"
            aria-atomic="true"
            ref="liveRegion"
          ></div>
          <form method="post" action="/cart/add" id="${this._escapeAttribute(formId)}" data-type="add-to-cart-form">
            <input type="hidden" name="form_type" value="product">
            <input type="hidden" name="utf8" value="&#x2713;">
            <input
              type="hidden"
              name="id"
              ref="variantId"
              value="${this._escapeAttribute(variant.id || '')}"
              ${canAddToCart ? '' : 'disabled'}
            >
            <input type="hidden" name="quantity" value="1">
            <add-to-cart-component
              ref="addToCartButtonContainer"
              data-product-variant-media="${this._escapeAttribute(productVariantMedia)}"
              data-add-to-cart-animation="${window.Theme?.settings?.addToCartAnimation || 'false'}"
            >
              <button
                type="submit"
                name="add"
                ref="addToCartButton"
                on:click="/handleClick"
                class="button rebuy-product-add-on-card__button"
                ${canAddToCart ? '' : 'disabled'}
              >
                <span class="add-to-cart-text">
                  <span class="add-to-cart-text__content">${this._escapeHtml(buttonLabel)}</span>
                </span>
                <span aria-hidden="true" class="add-to-cart-text--added">
                  <span>${this._escapeHtml(window.Theme?.translations?.added || 'Added')}</span>
                </span>
              </button>
            </add-to-cart-component>
          </form>
        </product-form-component>
      </div>
    `;
  }

  _getRebuyAddOnTitle(title = '') {
    const formattedTitle = this._getRebuyAddOnTitleWithoutSize(title);
    return formattedTitle
      .replace(/\s+(Sweet Potato Chips|Kettle Chips|Super Veggie Straws|Veggie Straws|Mixed Snacks).*$/i, '')
      .trim();
  }

  _getRebuyAddOnSubtitle(title = '') {
    return (
      this._getRebuyAddOnTitleWithoutSize(title).match(
        /(Sweet Potato Chips|Kettle Chips|Super Veggie Straws|Veggie Straws|Mixed Snacks)/i
      )?.[0] || ''
    );
  }

  _getRebuyAddOnTitleWithoutSize(title = '') {
    return title
      .replace(/\d+(?:\.\d+)?oz\s*\(Pack of \d+\)/i, '')
      .replace(/\s+in Avocado Oil.*$/i, '')
      .trim();
  }

  _getRebuyAddOnSize(title = '') {
    return title.match(/\d+(?:\.\d+)?oz\s*\(Pack of \d+\)/i)?.[0] || '';
  }

  _formatRebuyPrice(price) {
    if (price == null || price === '') return '';
    const numericPrice = Number(price);
    if (Number.isNaN(numericPrice)) return String(price);
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(numericPrice);
  }

  _imageUrlWithWidth(src, width) {
    if (!src) return '';
    const url = new URL(src, window.location.origin);
    url.searchParams.set('width', width.toString());
    return url.toString();
  }

  _escapeHtml(value = '') {
    return String(value)
      .replaceAll('&', '&amp;')
      .replaceAll('<', '&lt;')
      .replaceAll('>', '&gt;')
      .replaceAll('"', '&quot;')
      .replaceAll("'", '&#39;');
  }

  _escapeAttribute(value = '') {
    return this._escapeHtml(value);
  }

  /**
   * Build the search view URL for Rebuy recommendations
   * @param {string[]} handles
   */
  _buildRebuySearchUrl(handles) {
    const flags = [];
    const {
      layoutType,
      carouselOnMobile,
      iconsStyle,
      iconsShape,
      sectionWidth,
      displayMode,
      addButtonLabel,
      fallbackCollection,
      maxProducts,
    } = this.dataset;

    if (layoutType) flags.push(`layout:${layoutType}`);
    if (carouselOnMobile != null) flags.push(`mobile:${carouselOnMobile}`);
    if (iconsStyle) flags.push(`icons:${iconsStyle}`);
    if (iconsShape) flags.push(`shape:${iconsShape}`);
    if (sectionWidth) flags.push(`width:${sectionWidth}`);
    if (displayMode) flags.push(`display:${displayMode}`);
    if (addButtonLabel) flags.push(`add_label:${addButtonLabel}`);
    if (fallbackCollection) flags.push(`fallback:${fallbackCollection}`);
    if (maxProducts) flags.push(`max:${maxProducts}`);

    const query = [...handles, ...flags].map((value) => encodeURIComponent(value)).join('+');
    return `/search?view=rebuy-recommendations&q=${query}`;
  }

  /**
   * Fetches the recommendations and cached the result for future use
   * @param {string} productId
   * @param {string | undefined} sectionId
   * @param {string | undefined} intent
   * @returns {Promise<{ success: true, data: string } | { success: false, status: number }>}
   */
  async _fetchCachedRecommendations(productId, sectionId, intent) {
    const url = `${this.dataset.url}&product_id=${productId}&section_id=${sectionId}&intent=${intent}`;

    const cachedResponse = this.#cachedRecommendations[url];
    if (cachedResponse) {
      return { success: true, data: cachedResponse };
    }

    this.#activeFetch?.abort();
    this.#activeFetch = new AbortController();

    try {
      const response = await fetch(url, { signal: this.#activeFetch.signal });
      if (!response.ok) {
        return { success: false, status: response.status };
      }

      const text = await response.text();
      this.#cachedRecommendations[url] = text;
      return { success: true, data: text };
    } finally {
      this.#activeFetch = null;
    }
  }

  /**
   * Handle errors in a consistent way
   * @param {Error} error
   */
  _handleError(error) {
    console.error('Product recommendations error:', error.message);
    this.classList.add('hidden');
    this.dataset.error = 'Error loading product recommendations';
  }
}

if (!customElements.get('product-recommendations')) {
  customElements.define('product-recommendations', ProductRecommendations);
}
